import { useState } from 'react';

export default function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6s0hitsbgrc47fimprg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', projectType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact-form" className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Request Your <span className="text-amber-500">Free Estimate</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get started on your dream countertops today. Our experts will provide a detailed quote tailored to your project.
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">Free Consultation</div>
                  <div className="text-gray-400">No obligation, expert advice</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">Fast Response</div>
                  <div className="text-gray-400">We'll contact you within 24 hours</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">Competitive Pricing</div>
                  <div className="text-gray-400">10% off for veterans & first responders</div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gray-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <i className="ri-phone-line text-2xl text-amber-500"></i>
                <div>
                  <div className="text-sm text-gray-400">Call Us</div>
                  <a href="tel:801-706-1610" className="text-lg font-semibold hover:text-amber-500 transition-colors">
                    801-706-1610
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-phone-line text-2xl text-amber-500"></i>
                <div>
                  <div className="text-sm text-gray-400">Alternative</div>
                  <a href="tel:801-803-0804" className="text-lg font-semibold hover:text-amber-500 transition-colors">
                    801-803-0804
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                  placeholder="(801) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  required
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                >
                  <option value="">Select a project type</option>
                  <option value="Kitchen Countertops">Kitchen Countertops</option>
                  <option value="Bathroom Vanity">Bathroom Vanity</option>
                  <option value="Commercial Project">Commercial Project</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all resize-none"
                  placeholder="Tell us about your project... (max 500 characters)"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-amber-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Get Free Estimate'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                  Thank you! We'll contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  Something went wrong. Please call us at 801-706-1610.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}